import React, { useMemo } from 'react';
import { Label, Card, CardBody, Alert, Button, Row, Col } from 'reactstrap';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import { SectionHeader, RespondMessageBubble } from '../ui';
import {
  COUNTER_ACCENT,
  COUNTER_HIGHLIGHT_BG,
  SAME_VALUE_COLOR,
} from '../constants';
import AgreeLockButton from '../clause/AgreeLockButton';
import {
  getResponseSortTime,
  toLockedFlag,
  canAgreeLastCounter,
} from '../respond/helpers';
const EMPTY_COUNTER_BADGE_STYLE = {
  backgroundColor: '#ADB5BD',
  color: '#fff',
};

const ValueField = ({ label, value }) => (
  <div className='border-start border-2 border-primary ps-3'>
    <div className='text-muted small fw-semibold mb-1'>{label}</div>
    <div className='text-dark fs-13 mb-0 lh-base'>{value || '-'}</div>
  </div>
);

const isSameCounterDisplay = (original, counter) =>
  String(original ?? '').trim() === String(counter ?? '').trim();

const CounterField = ({ label, value, unchanged }) => {
  const color = unchanged ? SAME_VALUE_COLOR : COUNTER_ACCENT;
  return (
    <div className='ps-3' style={{ borderLeft: `2px solid ${color}` }}>
      <div className='small fw-semibold mb-1' style={{ color }}>
        {label}
      </div>
      <div className='fs-13 mb-0 lh-base fw-semibold' style={{ color }}>
        {value || '-'}
      </div>
    </div>
  );
};

const CounterValueGrid = ({
  label,
  counterDisplay,
  showPartyType,
  partyTypeCounter,
  isValueSame,
  isPartyTypeSame,
}) => (
  <Row className='gy-3'>
    <Col sm={showPartyType ? 6 : 12} md={showPartyType ? 6 : 12}>
      <CounterField
        label={label}
        value={counterDisplay}
        unchanged={isValueSame}
      />
    </Col>
    {showPartyType && (
      <Col sm={6} md={6}>
        <CounterField
          label='Operation Tolerance Options'
          value={partyTypeCounter}
          unchanged={isPartyTypeSame}
        />
      </Col>
    )}
  </Row>
);

const CounterNegotiationCard = ({
  title,
  subtitle,
  partyType,
  partyTypeCounter,
  label,
  currentDisplay,
  counterDisplay,
  hasCounter: hasCounterProp,
  isExpanded,
  onToggleExpand,
  isEditing,
  editable,
  isSaving,
  isDraftDirty,
  onStartEdit,
  onCancel,
  onSave,
  onDelete,
  onRespond,
  historyItems = [],
  isEvaluatorView = false,
  extraFields = [],
  onLockResponse,
  isRespondClosed = false,
  proposeCounterLabel = 'Propose Counter Value',
  children,
}) => {
  const hasCounter = hasCounterProp ?? !!(counterDisplay || partyTypeCounter);
  const sortedHistory = useMemo(
    () =>
      [...(historyItems ?? [])].sort(
        (a, b) => getResponseSortTime(a) - getResponseSortTime(b)
      ),
    [historyItems]
  );
  const isThreadLocked = toLockedFlag(
    sortedHistory[sortedHistory.length - 1]?.isLocked
  );
  const canRespond =
    typeof onRespond === 'function' && !isThreadLocked && !isRespondClosed;
  const showProposeButton =
    editable && !hasCounter && !isEditing && !isRespondClosed;
  const showPartyType = partyType !== undefined;
  const isValueSame = isSameCounterDisplay(currentDisplay, counterDisplay);
  const isPartyTypeSame = isSameCounterDisplay(partyType, partyTypeCounter);

  const showHistory = !editable && isExpanded && sortedHistory.length > 0;
  const showRespondEditor =
    !editable && isExpanded && isEditing && !isThreadLocked && !isRespondClosed;

  return (
    <Card className='border shadow-sm mb-0 bg-white'>
      <CardBody>
        <SectionHeader title={title} subtitle={subtitle} />

        <div className='d-flex flex-column flex-sm-row justify-content-between align-items-start gap-3 mb-3'>
          <div className='flex-grow-1 min-w-0'>
            <Row className='gy-4'>
              {extraFields.map(field => (
                <Col key={field.label} sm={6} md={6}>
                  <ValueField label={field.label} value={field.value || '-'} />
                </Col>
              ))}
              <Col sm={showPartyType || extraFields.length > 0 ? 6 : 12} md={6}>
                <ValueField label={label} value={currentDisplay || '-'} />
              </Col>
              {showPartyType && (
                <Col sm={6} md={6}>
                  <ValueField
                    label='Operation Tolerance Options'
                    value={partyType || '-'}
                  />
                </Col>
              )}
            </Row>
          </div>

          <div className='d-flex align-items-center gap-2 flex-shrink-0'>
            {showProposeButton && (
              <Button
                color='light'
                size='sm'
                className='border bg-white text-muted px-3 py-1 d-flex align-items-center gap-1 fs-13 rounded-2 shadow-sm'
                onClick={() => {
                  if (!isExpanded) onToggleExpand();
                  onStartEdit();
                }}
              >
                <span>Propose Counter</span>
                <i className='ri-chat-3-line fs-14 align-middle ms-1'></i>
              </Button>
            )}
          </div>
        </div>

        <div className='border-top pt-3 d-flex justify-content-between align-items-center'>
          <div
            className='d-flex align-items-center gap-2'
            style={{ cursor: 'pointer' }}
            onClick={onToggleExpand}
          >
            <span
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                isExpanded
                  ? 'bg-primary-subtle text-primary'
                  : 'bg-light text-muted'
              }`}
              style={{ width: '28px', height: '28px' }}
            >
              <i
                className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
              ></i>
            </span>
            {sortedHistory.length > 0 ? (
              <>
                <span
                  className='bg-danger text-white rounded-pill d-inline-flex align-items-center justify-content-center fw-semibold fs-11'
                  style={{
                    minWidth: '20px',
                    height: '20px',
                    padding: sortedHistory.length > 9 ? '0 6px' : 0,
                  }}
                >
                  {sortedHistory.length}
                </span>
                <span className='fw-semibold text-dark fs-13'>Response</span>
              </>
            ) : editable && !hasCounter ? (
              <span
                className='rounded-pill px-2 py-1 fs-11 fw-semibold'
                style={EMPTY_COUNTER_BADGE_STYLE}
              >
                No counter proposals
              </span>
            ) : (
              <span className='text-muted fs-13'>No responses yet</span>
            )}
          </div>

          {canRespond && (
            <Button
              color='light'
              size='sm'
              className='border bg-white text-muted px-3 py-1 d-flex align-items-center gap-1 fs-13 rounded-2 shadow-sm'
              onClick={() => {
                if (!isExpanded && onToggleExpand) onToggleExpand();
                onRespond();
              }}
            >
              <span>Respond</span>
              <i className='ri-chat-3-line fs-14 align-middle ms-1'></i>
            </Button>
          )}
        </div>

        {editable && isExpanded && (
          <div className='mt-3'>
            {hasCounter && !isEditing ? (
              <div
                className='p-3 rounded-3 mt-2'
                style={{
                  borderLeft: `3px solid ${COUNTER_ACCENT}`,
                  backgroundColor: COUNTER_HIGHLIGHT_BG,
                }}
              >
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div
                    className='fw-bold fs-13'
                    style={{ color: COUNTER_ACCENT }}
                  >
                    Proposed Counter Value
                  </div>
                </div>
                <div className='mb-3'>
                  <CounterValueGrid
                    label={label}
                    counterDisplay={counterDisplay}
                    showPartyType={showPartyType}
                    partyTypeCounter={partyTypeCounter}
                    isValueSame={isValueSame}
                    isPartyTypeSame={isPartyTypeSame}
                  />
                </div>

                {!isEvaluatorView && (
                  <div className='d-flex align-items-center gap-2'>
                    <Button
                      color='primary'
                      size='sm'
                      className='fw-medium fs-13 px-3'
                      onClick={onStartEdit}
                    >
                      Revise
                    </Button>
                    <Button
                      color='danger'
                      size='sm'
                      className='fw-medium fs-13 px-3'
                      onClick={onDelete}
                    >
                      Withdraw
                    </Button>
                  </div>
                )}
              </div>
            ) : isEditing ? (
              <div className='mt-2 p-3 bg-light rounded-3 border'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                    {proposeCounterLabel}
                  </Label>
                  {isDraftDirty && (
                    <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                      <i className='ri-error-warning-line fs-14'></i>
                      Unsaved Changes
                    </span>
                  )}
                </div>

                <div className='mb-3 bg-white p-2 border rounded-3'>
                  {children}
                </div>

                <div className='d-flex gap-2 justify-content-end'>
                  <Button
                    color='light'
                    size='sm'
                    className='border bg-white px-3'
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    color='primary'
                    size='sm'
                    className='px-3'
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Submitting...' : 'Submit Counter'}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {!editable && isExpanded && hasCounter && (
          <Alert
            className='alert-solid alert-label-icon mb-0 mt-3 border-0'
            style={{
              backgroundColor: COUNTER_HIGHLIGHT_BG,
              color: COUNTER_ACCENT,
            }}
            fade={false}
          >
            <i className='ri-chat-history-line label-icon fw-bold'></i>
            <div>
              <div
                className='fw-semibold mb-2 fs-13'
                style={{ color: COUNTER_ACCENT }}
              >
                Final Counter Value
              </div>
              <CounterValueGrid
                label={label}
                counterDisplay={counterDisplay}
                showPartyType={showPartyType}
                partyTypeCounter={partyTypeCounter}
                isValueSame={isValueSame}
                isPartyTypeSame={isPartyTypeSame}
              />
            </div>
          </Alert>
        )}

        {showHistory && (
          <div className='mt-3'>
            {sortedHistory.map((item, index) => {
              const evaluatorItem = item?.isPertamina === true;
              const metaDate = formatDateToddmmyyhhmm(
                item?.updatedDate || item?.createdDate
              );
              const authorLabel = evaluatorItem
                ? item.createdByLabel || 'PT Pertamina Patra Niaga'
                : item.createdByLabel || 'Counter';
              const lastItem = sortedHistory[sortedHistory.length - 1];
              const canManage =
                !isRespondClosed &&
                lastItem &&
                String(item.id) === String(lastItem.id) &&
                (isEvaluatorView ? evaluatorItem : !evaluatorItem) &&
                !item.isLocked;
              const isOwnEditing = canManage && isEditing;

              if (isOwnEditing) return null;

              return (
                <RespondMessageBubble
                  key={item.id ?? `${title}-${index}-${item.display}`}
                  isEvaluator={evaluatorItem}
                  content={item.display}
                  authorLabel={authorLabel}
                  metaDate={metaDate}
                  footer={
                    <>
                      {canAgreeLastCounter(sortedHistory, item) &&
                        (!isRespondClosed || item.isLocked) && (
                          <div className='mt-3 pt-2 border-top'>
                            <AgreeLockButton
                              embedded
                              isLocked={item.isLocked}
                              onLock={
                                !isRespondClosed &&
                                isEvaluatorView &&
                                item.id &&
                                onLockResponse
                                  ? locked => onLockResponse(item.id, locked)
                                  : undefined
                              }
                              disabled={isSaving}
                            />
                          </div>
                        )}
                      {canManage &&
                      !item.isLocked &&
                      !isRespondClosed &&
                      (onStartEdit || onDelete) ? (
                        <div className='mt-3 pt-2 border-top d-flex align-items-center gap-2'>
                          {onStartEdit && (
                            <Button
                              color='light'
                              size='sm'
                              className='border bg-white text-primary fw-medium fs-13 flex-grow-1'
                              onClick={onStartEdit}
                            >
                              Edit
                            </Button>
                          )}
                          {onDelete && (
                            <Button
                              color='light'
                              size='sm'
                              className='border bg-white text-danger fw-medium fs-13 flex-grow-1'
                              onClick={onDelete}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </div>
        )}

        {showRespondEditor && (
          <div className='mt-2'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                Respond Notes
              </Label>
              {isDraftDirty && (
                <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                  <i className='ri-error-warning-line fs-14'></i>
                  Unsaved Changes
                </span>
              )}
            </div>

            {children}

            <div className='d-flex gap-2 mt-3 justify-content-end'>
              <Button
                color='light'
                className='border bg-white text-dark fw-semibold px-4 rounded-3'
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                color='primary'
                className='fw-semibold px-4 rounded-3'
                onClick={onSave}
                disabled={isSaving || !isDraftDirty}
              >
                {isSaving ? 'Saving...' : 'Send Response'}
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CounterNegotiationCard;
